import Color from "color";
import "./ResultsVoteAfterSendForm.scss";
import { Chart, DoughnutController, ArcElement } from "chart.js";
import { I18n } from "~/js/tools/i18n";
/**
 * @typedef {Object} optionResult
 * @property {string} label
 * @property {string} key
 * @property {number} order
 * @property {number | null} limitcount
 * @property {number | null} count
 * */

/**
 * @param {Element} elem - Элемент DOM.
 * @param {Object} settings - Настройки.
 * @param {Object.<string, { label: string, optionLimitEnable: boolean, results: optionResult[]}>} settings.results - Объект результатов, где ключ — это строка (field_id), а значение — объект с полями key и count.
 * @param formData {DataForm}
 */
export const setVoteResult = function (elem) {
  let settings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    results: null,
    params: null
  };
  let {
    style
  } = arguments.length > 2 ? arguments[2] : undefined;
  const voteResultEl = document.createElement("div");
  voteResultEl.classList.add("qform-vote-results");
  voteResultEl.classList.toggle("--qform-dark-theme", !!style.formStyleCommon.theme);
  const fields = Object.values(settings.results).map(field => {
    field.results.sort((a, b) => a.order - b.order);
    return field;
  });
  voteResultEl.append(...fields.map(res => createSummaryGraph(res, style.formStyleVote)));
  elem.appendChild(voteResultEl);
};

/**
 * @param res {{label: string, optionLimitEnable: boolean, results: optionResult[]}}
 * @param design {formStyleVote}
 **/
function createSummaryGraph(res, design) {
  const resEl = document.createElement("div");
  resEl.classList.add("qform-summary");
  const fieldNameEl = document.createElement("div");
  fieldNameEl.classList.add("qform-summary-title");
  fieldNameEl.innerText = res.label;
  const graphEl = document.createElement("div");
  graphEl.classList.add("qform-summary-graph");
  /* Меняем местами график и список результатов от значения расположения диаграммы*/
  graphEl.classList.toggle("--reverse", design.direction === 1);
  const resListEl = document.createElement("ul");
  resListEl.classList.add("qform-summary-graph-list");
  const colors = colorScheme(design.diagramColor, res.results.length);
  /*
   * 1 - Отображать все виды диаграммы
   * 2 - Только линейную
   * 3 - Только круговую
   **/
  resListEl.append(...getListResult(res.results, colors, res.optionLimitEnable, [1, 2].includes(design.diagramType), design.resultFormat));
  graphEl.append(resListEl);
  [1, 3].includes(design.diagramType) && graphEl.append(getGraphResult(res.results, colors));
  resEl.append(fieldNameEl, graphEl);
  return resEl;
}

/**
 * @param options {Array<optionResult>}
 * @param colorScheme {Array<string>}
 * @param optionLimitEnable {Boolean}
 * @param showProgress {Boolean}
 * @param showResult
 * @return Array<HTMLLIElement>
 */
function getListResult(options, colorScheme) {
  let optionLimitEnable = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  let showProgress = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  let showResult = arguments.length > 4 ? arguments[4] : undefined;
  const optionsElements = [];
  const totalCount = options.reduce((acc, item) => acc + item.count, 0);
  options.forEach((option, idx) => {
    const optionEl = document.createElement("li");
    optionEl.setAttribute("data-current", option.count);
    optionEl.setAttribute("data-total", optionLimitEnable ? option.limitcount : totalCount);
    optionEl.classList.add("qform-summary-graph-list__option");
    optionEl.style.cssText = `
      --color: ${colorScheme[idx]};
      --value: ${getPercent(option.count, optionLimitEnable ? option.limitcount : totalCount)}%;
    `;
    const valueText = {
      1: `${Math.round(getPercent(option.count, optionLimitEnable ? option.limitcount : totalCount))}%`,
      2: option.limitcount && optionLimitEnable ? `${option.count}/${option.limitcount}` : `${option.count}`
    };
    const optionValueEl = document.createElement("div");
    optionValueEl.classList.add("qform-summary-graph-list__option-value");
    optionValueEl.innerHTML = `<span>${option.label}</span>`;
    if (valueText[showResult]) {
      const optionValueTotal = document.createElement("span");
      optionValueTotal.textContent = `${valueText[showResult]}`;
      optionValueEl.append(optionValueTotal);
    }
    optionEl.append(optionValueEl);
    if (showProgress) {
      const optionProgressEl = document.createElement("div");
      optionProgressEl.classList.add("qform-summary-graph-list__option-progress");
      optionEl.append(optionProgressEl);
    }
    optionsElements.push(optionEl);
  });
  return optionsElements;
}

/**
 * @param options {Array<optionResult>}
 * @param colorScheme {Array<string>}
 */
function getGraphResult(options, colorScheme) {
  const $i18n = new I18n();
  const totalAnswers = options.reduce((acc, item) => acc + item.count, 0);
  Chart.register(DoughnutController, ArcElement);
  const containerEl = document.createElement("div");
  containerEl.classList.add("qform-summary-graph-container");
  /**
   * @type {ChartConfiguration}
   **/
  const chartConfig = {
    type: "doughnut",
    data: {
      labels: options.map(item => item.label),
      datasets: [{
        data: options.map(item => item.count),
        backgroundColor: colorScheme,
        hoverBackgroundColor: colorScheme,
        borderWidth: 0
      }]
    },
    options: {
      animation: true,
      hover: {
        mode: null
      },
      plugins: {
        tooltip: {
          enabled: false
        },
        legend: {
          display: false
        }
      },
      offset: 15
    }
  };
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = 400;
  canvas.height = 400;
  const totalAnswersElement = document.createElement("div");
  totalAnswersElement.classList.add("qform-summary-graph-list__total-answers");
  totalAnswersElement.innerHTML = `
    <span>${totalAnswers.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}</span><br><span>${pluralize(totalAnswers, ["ответ", "ответа", "ответов"])}</span>`;
  containerEl.append(canvas, totalAnswersElement);
  new Chart(ctx, chartConfig);
  return containerEl;
}
/**
 * Функция расчета цветовой схемы
 * @param color {string} - hex
 * @param length {number}
 * @return Array<string> - массив цветов в hex
 * */
function colorScheme(color, length) {
  if (length === 1) {
    return [color];
  }
  const colorInstance = Color(color);
  const [h, s, l] = colorInstance.hsl().array();
  return Array.from({
    length
  }, (_, i) => {
    const shift = h - (1 + hueShift(length) * i) % 360;
    return Color.hsl(shift, s, l).hex();
  });
}

/**
 * Функция расчет смещения цвета
 * @param length {number}
 * @return number
 * */
function hueShift(length) {
  return length === 0 ? 0 : 360 / length;
}

/**
 * Расчет процентов
 * @param part {number}
 * @param total {number}
 * @return number
 * */
function getPercent(part, total) {
  if (!total) return 0;
  return part / total * 100;
}
function pluralize(count, forms) {
  // forms = ['форма', 'формы', 'форм']
  let n = Math.abs(count) % 100;
  let last = n % 10;
  if (n >= 11 && n <= 19) {
    return forms[2];
  }
  if (last === 1) {
    return forms[0];
  } else if (last >= 2 && last <= 4) {
    return forms[1];
  } else {
    return forms[2];
  }
}